import type { FC } from "react";
import { createContext, useContext, ReactNode, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LanguageService } from "src/services/LanguageService";
import { useGetEnabledLocales, useGetDefaultLocale, type Locale } from "src/api/locales/useLocales";
import { useDirectionSync } from "src/utils/useDirectionSync";
import { Loading } from "src/components/utils/Loading";

interface LanguageContextValue {
    currentLanguage: string;
    enabledLanguages: Locale[];
    isLoading: boolean;
    changeLanguage: (languageCode: string) => void;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

interface LanguageProviderProps {
    children: ReactNode;
}

export const LanguageProvider: FC<LanguageProviderProps> = ({ children }) => {
    const { i18n } = useTranslation();

    const [currentLanguage, setCurrentLanguage] = useState<string | null>(() => LanguageService.getCurrentLanguage());
    const [isReady, setIsReady] = useState(false);

    const enabledQuery = useGetEnabledLocales();
    const defaultQuery = useGetDefaultLocale();
    const enabledLocales = enabledQuery.data;
    const defaultLocale = defaultQuery.data;
    const localesLoaded = enabledQuery.isSuccess && defaultQuery.isSuccess;
    const localesSettled =
        (enabledQuery.isSuccess || enabledQuery.isError) && (defaultQuery.isSuccess || defaultQuery.isError);

    useDirectionSync(currentLanguage || "en");

    useEffect(() => {
        if (currentLanguage) {
            let cancelled = false;
            i18n.changeLanguage(currentLanguage).finally(() => {
                if (!cancelled) setIsReady(true);
            });
            return () => {
                cancelled = true;
            };
        }

        if (!localesSettled) {
            return;
        }

        const apiResponse = {
            enabledLanguages: (enabledLocales ?? []).map((locale) => locale.code).filter(Boolean) as string[],
            defaultLanguage: defaultLocale?.code || "en",
        };

        if (localesLoaded) {
            LanguageService.saveToCache(apiResponse);
        }

        const optimalLanguage = LanguageService.calculateOptimalLanguage({
            ...apiResponse,
            timestamp: Date.now(),
        });

        setCurrentLanguage(optimalLanguage);
    }, [currentLanguage, localesSettled, localesLoaded, enabledLocales, defaultLocale, i18n]);

    const changeLanguage = (languageCode: string) => {
        LanguageService.changeLanguage(languageCode);
    };

    const contextValue: LanguageContextValue = {
        currentLanguage: currentLanguage || "en",
        enabledLanguages: enabledLocales || [],
        isLoading: !isReady,
        changeLanguage,
    };

    return <LanguageContext.Provider value={contextValue}>{isReady ? children : <Loading />}</LanguageContext.Provider>;
};

export const useLanguage = (): LanguageContextValue => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error("useLanguage must be used within a LanguageProvider");
    }
    return context;
};
